import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Star } from "lucide-react";
import Reveal from "@/components/landing/Reveal";

const stats = [
  { value: 340, prefix: "+", suffix: " %", decimals: 0, label: "de conversions en plus en moyenne après refonte" },
  { value: 1.5, prefix: "< ", suffix: " s", decimals: 1, label: "de temps de chargement moyen, mobile inclus" },
  { value: 100, suffix: " %", decimals: 0, label: "d'hébergement, sécurité et maintenance inclus" },
  { value: 24, prefix: "< ", suffix: " h", decimals: 0, label: "de délai de réponse au support prioritaire" },
];

const testimonials = [
  {
    quote:
      "Notre site génère maintenant trois fois plus de demandes de soumission qu'avant. Et je n'ai jamais eu à toucher à quoi que ce soit de technique.",
    name: "Martin L.",
    role: "Entrepreneur en construction",
    initials: "ML",
  },
  {
    quote:
      "Zéro chèque de départ, un site rapide et professionnel en ligne en deux semaines. Le meilleur investissement marketing qu'on ait fait.",
    name: "Sophie D.",
    role: "Clinique dentaire",
    initials: "SD",
  },
  {
    quote:
      "Le support répond en quelques heures et les modifications sont en ligne le lendemain. C'est comme avoir un développeur à temps plein, sans le salaire.",
    name: "Jean-Philippe R.",
    role: "Courtier immobilier",
    initials: "JR",
  },
];

function Counter({ value, decimals = 0, prefix = "", suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState((0).toFixed(decimals).replace(".", ","));

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(v.toFixed(decimals).replace(".", ",")),
    });
    return () => controls.stop();
  }, [inView, value, decimals]);

  return (
    <span ref={ref} className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
      {prefix}
      <span className="text-emerald-400">{display}</span>
      {suffix}
    </span>
  );
}

export default function SocialProof() {
  return (
    <section className="relative py-20 lg:py-24" data-testid="social-proof-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1}>
              <div className="border-l border-white/10 pl-6" data-testid={`stat-${i}`}>
                <Counter value={s.value} decimals={s.decimals} prefix={s.prefix} suffix={s.suffix} />
                <p className="mt-3 text-sm leading-relaxed text-gray-400">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 grid gap-6 md:grid-cols-3 lg:gap-8">
          {testimonials.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.12}>
              <figure
                className="glass flex h-full flex-col rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1 hover:border-emerald-400/25"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex gap-1" aria-label="5 étoiles">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="h-4 w-4 fill-emerald-400 text-emerald-400" />
                  ))}
                </div>
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-gray-300">
                  « {t.quote} »
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-3">
                  <span className="font-display flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/15 text-sm font-bold text-emerald-300">
                    {t.initials}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-white">{t.name}</p>
                    <p className="text-xs text-gray-500">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
